import { useHubCtas } from "@/hooks/useHubCtas";
import { useUserSubscriptionState } from "@/hooks/useUserSubscriptionState";
import { useAuth } from "@/lib/AuthContext";
import { useAppleTrial } from "@/lib/purchases/useAppleTrial";
import { isTrialCourseCtaEligible, TRIAL_CTA_TARGET } from "@/lib/trialCourseCta";

/**
 * "Jazz Piano First Steps" trial CTA state for the current user.
 * - `eligible`: the user is in the trial audience (see lib/trialCourseCta.ts).
 *   Trial comes from the WooCommerce subscription row (web signups) or the
 *   RevenueCat entitlement (Apple signups on iOS).
 * - `active`: eligible AND at least one enabled trial CTA row exists in the
 *   CMS — only then are the other banners (promo, hub CTAs) suppressed, so
 *   deploying the app before the CMS row changes nothing for anyone.
 * Both are false until subscription state, preferences, Apple trial state and
 * CTAs have settled.
 */
export function useTrialCourseCta(userId: string | undefined): {
  eligible: boolean;
  active: boolean;
} {
  const { user, preferenceMetadata, preferencesLoading } = useAuth();
  const { data: row = null, isSuccess } = useUserSubscriptionState({ userId });
  const appleTrial = useAppleTrial(userId);

  const resolved =
    Boolean(userId) && isSuccess && !preferencesLoading && appleTrial.resolved;
  const eligible =
    resolved &&
    isTrialCourseCtaEligible({
      subscription: row,
      userCreatedAt: user?.created_at ?? null,
      preferenceMetadata,
      appleTrial,
    });

  // Same query key as SubscriptionHubCtas → shared cache, no extra request.
  const { data: ctas = [] } = useHubCtas({
    userId,
    subscription: row,
    subscriptionResolved: isSuccess,
    userCreatedAt: user?.created_at ?? null,
    preferenceMetadata,
    preferencesLoading,
    appleTrial,
    enabled: eligible,
  });

  const active =
    eligible && ctas.some((cta) => cta.target === TRIAL_CTA_TARGET);

  return { eligible, active };
}
